/**
 * SarinahCouponBrowser — Browse Sarinah Coupon Templates
 *
 * Called by: Retail.jsx (Sarinah Coupons section)
 * Uses: sarinahApi from config/api.js → GET /sarinah/coupons/templates
 * Displays available coupon templates from Sarinah API v1.1
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Card, CardBody,
  Button, Badge, useToast, Spinner, SimpleGrid,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Tag, Tooltip, Divider,
} from '@chakra-ui/react';
import { FiRefreshCw, FiTag, FiCalendar, FiPercent, FiDollarSign, FiClock, FiDownload } from 'react-icons/fi';
import { sarinahApi } from '../config/api';

const formatIDR = (num) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);

const ActiveBadge = ({ active }) => (
  <Badge colorScheme={active ? 'green' : 'gray'} fontSize="xs" px={2} py={0.5} borderRadius="full">
    {active ? 'Active' : 'Inactive'}
  </Badge>
);

const VoucherTypeBadge = ({ type }) => {
  const map = {
    PERCENTAGE_VOUCHER: { color: 'purple', label: 'Percentage' },
    FIXED_VOUCHER: { color: 'blue', label: 'Fixed' },
  };
  const cfg = map[type] || { color: 'gray', label: type || 'Unknown' };
  return <Badge colorScheme={cfg.color} fontSize="xs">{cfg.label}</Badge>;
};

const ScanTypeBadge = ({ scanType }) => {
  if (!scanType) return <Badge colorScheme="gray" fontSize="xs">N/A</Badge>;
  return <Badge colorScheme="teal" fontSize="xs">{scanType}</Badge>;
};

const ValidDaysDisplay = ({ days }) => {
  if (!days || days.length === 0) return <Text fontSize="xs" color="gray.500">—</Text>;
  if (days.length === 7) return <Badge colorScheme="green" fontSize="xs">Every Day</Badge>;
  const dayMap = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };
  return (
    <HStack spacing={1} flexWrap="wrap">
      {days.map(d => (
        <Badge key={d} colorScheme="blue" variant="subtle" fontSize="xs" px={1.5}>{dayMap[d] || d.slice(0, 3)}</Badge>
      ))}
    </HStack>
  );
};

const SarinahCouponBrowser = () => {
  const toast = useToast();
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCoupons = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sarinahApi.get('/coupons/templates');
      const items = res?.data?.data || [];
      setCoupons(items);
      if (items.length === 0) {
        toast({ title: 'No coupons found', description: 'Sarinah returned an empty list.', status: 'info', duration: 3000, isClosable: true });
      } else {
        toast({ title: 'Coupons loaded', description: `${items.length} template(s) from Sarinah`, status: 'success', duration: 3000, isClosable: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      setError(msg);
      toast({ title: 'Failed to load coupons', description: msg, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const [importingId, setImportingId] = useState(null);

  const handleImport = useCallback(async (coupon) => {
    setImportingId(coupon.couponId);
    try {
      const res = await sarinahApi.post('/import', { couponId: coupon.couponId });
      toast({
        title: 'Coupon imported',
        description: res?.data?.message || `"${coupon.name}" imported as Retail product`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      toast({ title: 'Import failed', description: msg, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setImportingId(null);
    }
  }, [toast]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  return (
    <Box>
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            <FiTag size={18} />
            <Heading size="md">Sarinah Coupon Templates</Heading>
            <Badge colorScheme="orange" fontSize="xs" ml={1}>Sandbox</Badge>
          </HStack>
          <Button leftIcon={<FiRefreshCw />} size="sm" colorScheme="blue" variant="outline" onClick={fetchCoupons} isLoading={isLoading}>Refresh</Button>
        </HStack>
        <Divider />

        {isLoading && (
          <VStack py={8} spacing={3}><Spinner size="lg" color="blue.500" /><Text color="gray.500" fontSize="sm">Fetching coupons from Sarinah...</Text></VStack>
        )}

        {!isLoading && error && (
          <Card bg="red.50" border="1px" borderColor="red.200">
            <CardBody>
              <HStack spacing={3}>
                <FiDollarSign color="#E53E3E" />
                <VStack align="start" spacing={0}>
                  <Text fontWeight="semibold" color="red.700" fontSize="sm">Failed to load coupons</Text>
                  <Text color="red.600" fontSize="xs">{error}</Text>
                </VStack>
                <Button size="xs" colorScheme="red" variant="outline" onClick={fetchCoupons}>Retry</Button>
              </HStack>
            </CardBody>
          </Card>
        )}

        {!isLoading && !error && coupons.length > 0 && (
          <>
            {/* Summary Cards */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {coupons.map(coupon => (
                <Card key={coupon.couponId} border="1px" borderColor="gray.100" borderRadius="12px" boxShadow="sm">
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      <HStack justify="space-between">
                        <Text fontWeight="bold" fontSize="sm" noOfLines={2} flex={1}>{coupon.displayInfo?.description || coupon.name}</Text>
                        <ActiveBadge active={coupon.active} />
                      </HStack>
                      <HStack spacing={2}><VoucherTypeBadge type={coupon.voucherType} /><ScanTypeBadge scanType={coupon.scanType} /></HStack>
                      <Divider />
                      <HStack spacing={2}><FiPercent size={14} color="#805AD5" /><Text fontSize="sm" fontWeight="semibold" color="purple.600">{coupon.discountPercentage != null ? `${coupon.discountPercentage}% Off` : '—'}</Text></HStack>
                      <HStack spacing={2}><FiDollarSign size={14} color="#38A169" /><Text fontSize="xs" color="gray.600">Min Purchase: <Text as="span" fontWeight="medium">{coupon.usageRules?.minPayAmount?.value ? formatIDR(Number(coupon.usageRules.minPayAmount.value)) : '—'}</Text></Text></HStack>
                      {coupon.usageRules?.maxDiscount?.value && Number(coupon.usageRules.maxDiscount.value) > 0 && (
                        <HStack spacing={2}><FiDollarSign size={14} color="#E53E3E" /><Text fontSize="xs" color="gray.600">Max Discount: <Text as="span" fontWeight="medium">{formatIDR(Number(coupon.usageRules.maxDiscount.value))}</Text></Text></HStack>
                      )}
                      <HStack spacing={2}><FiCalendar size={14} color="#3182CE" /><ValidDaysDisplay days={coupon.validDays} /></HStack>
                      <HStack spacing={2}><FiClock size={14} color="#718096" /><Text fontSize="xs" color="gray.500">{coupon.startTime ? new Date(coupon.startTime).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'} — {coupon.endTime ? new Date(coupon.endTime).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</Text></HStack>
                      <Text fontSize="xs" color="gray.400">{coupon.displayInfo?.merchantName || 'Sarinah'} · ID: {coupon.couponId}</Text>
                      <Button
                        size="xs"
                        colorScheme="orange"
                        variant="solid"
                        leftIcon={<FiDownload />}
                        isLoading={importingId === coupon.couponId}
                        onClick={() => handleImport(coupon)}
                        isDisabled={!coupon.active}
                      >
                        {coupon.active ? 'Import as Product' : 'Inactive'}
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>

            {/* Detail Table */}
            <Box>
              <Heading size="sm" mb={2}>All Templates</Heading>
              <TableContainer border="1px" borderColor="gray.100" borderRadius="8px">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>ID</Th><Th>Name</Th><Th>Type</Th><Th isNumeric>Discount</Th>
                      <Th isNumeric>Min Purchase</Th><Th isNumeric>Max Discount</Th>
                      <Th>Valid Days</Th><Th>Active</Th><Th>Scan</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {coupons.map(coupon => (
                      <Tr key={coupon.couponId}>
                        <Td><Tag size="sm" colorScheme="gray">{coupon.couponId}</Tag></Td>
                        <Td><Tooltip label={coupon.displayInfo?.description || coupon.name}><Text fontSize="sm" fontWeight="medium" noOfLines={1} maxW="200px">{coupon.name}</Text></Tooltip></Td>
                        <Td><VoucherTypeBadge type={coupon.voucherType} /></Td>
                        <Td isNumeric><Text fontSize="sm" fontWeight="semibold" color="purple.600">{coupon.discountPercentage != null ? `${coupon.discountPercentage}%` : '—'}</Text></Td>
                        <Td isNumeric><Text fontSize="sm">{coupon.usageRules?.minPayAmount?.value ? formatIDR(Number(coupon.usageRules.minPayAmount.value)) : '—'}</Text></Td>
                        <Td isNumeric><Text fontSize="sm">{coupon.usageRules?.maxDiscount?.value && Number(coupon.usageRules.maxDiscount.value) > 0 ? formatIDR(Number(coupon.usageRules.maxDiscount.value)) : '—'}</Text></Td>
                        <Td><ValidDaysDisplay days={coupon.validDays} /></Td>
                        <Td><ActiveBadge active={coupon.active} /></Td>
                        <Td><ScanTypeBadge scanType={coupon.scanType} /></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}

        {!isLoading && !error && coupons.length === 0 && (
          <Card bg="gray.50" border="1px dashed" borderColor="gray.200" borderRadius="12px">
            <CardBody>
              <VStack spacing={3} py={6}>
                <FiTag size={32} color="#A0AEC0" />
                <Text color="gray.500" fontSize="sm" textAlign="center">No coupon templates available from Sarinah.</Text>
                <Button size="sm" leftIcon={<FiRefreshCw />} onClick={fetchCoupons} variant="ghost">Refresh</Button>
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

export default SarinahCouponBrowser;
